import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { db } from '../firebase'
import { collection, query, where, orderBy, limit, getDocs, startAfter } from 'firebase/firestore'
import { createDeviceService } from '../services/deviceService'
import { createSmartCardService } from '../services/smartCardService'
import AlertBadge from './AlertBadge'

export default function Reports() {
  const { userClaims, userPermissions, isSuperAdmin } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [transactions, setTransactions] = useState([])
  const [filteredTransactions, setFilteredTransactions] = useState([])
  const [paginatedTransactions, setPaginatedTransactions] = useState([])
  const [customerName, setCustomerName] = useState('')
  
  // Filter states
  const [filters, setFilters] = useState({
    dateRange: 'today', // days or 'custom'
    customStartDate: '',
    customEndDate: '',
    selectedDevices: [], // array of selected device IDs
    transactionType: 'all', // all, cash, pg, refund
    paymentMethod: 'all',
    searchTerm: ''
  })
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(50)
  const [totalPages, setTotalPages] = useState(0)
  
  // Available devices for filter
  const [devices, setDevices] = useState([])
  const [deviceObjects, setDeviceObjects] = useState([]) // Full device objects with location data
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState([])
  const [showDeviceDropdown, setShowDeviceDropdown] = useState(false)
  
  // Summary view toggle
  const [viewMode, setViewMode] = useState('detailed') // 'detailed' or 'summary'
  const [expandedDates, setExpandedDates] = useState(new Set()) // For expandable summary rows
  
  // Report type toggle (UPI/Cash vs Smart Card)
  const [reportType, setReportType] = useState('upi') // 'upi' or 'smartcard'
  const [smartCardTransactions, setSmartCardTransactions] = useState([])
  const [filteredSmartCardTransactions, setFilteredSmartCardTransactions] = useState([])
  const [paginatedSmartCardTransactions, setPaginatedSmartCardTransactions] = useState([])
  const [smartCardDevices, setSmartCardDevices] = useState([])
  const [smartCardLoading, setSmartCardLoading] = useState(false)
  const [smartCardCurrentPage, setSmartCardCurrentPage] = useState(1)
  const [smartCardTotalPages, setSmartCardTotalPages] = useState(0)

  // Sort states — default newest-first to match previous behaviour
  const [upiSort, setUpiSort] = useState({ column: 'datetime', direction: 'desc' })
  const [summarySort, setSummarySort] = useState({ column: 'date', direction: 'desc' })
  const [scSort, setScSort] = useState({ column: 'datetime', direction: 'desc' })

  // Generic sort helper
  const getSortedData = (data, sortState, valueGetter) => {
    if (!sortState.column) return data
    return [...data].sort((a, b) => {
      const aVal = valueGetter(a, sortState.column) ?? ''
      const bVal = valueGetter(b, sortState.column) ?? ''
      let cmp
      if (aVal instanceof Date && bVal instanceof Date) {
        cmp = aVal.getTime() - bVal.getTime()
      } else if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal
      } else {
        cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' })
      }
      return sortState.direction === 'asc' ? cmp : -cmp
    })
  }

  // Sort toggle handler
  const handleSortToggle = (setSort, column, resetPage) => {
    setSort(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }))
    resetPage()
  }

  // Reusable sortable <th>
  const SortableHeader = ({ label, column, sortState, onSort }) => (
    <th
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => onSort(column)}
    >
      {label}{' '}
      {sortState.column === column ? (
        <i
          className={`fas fa-sort-${sortState.direction === 'asc' ? 'up' : 'down'} text-primary ms-1`}
          style={{ fontSize: '0.7rem' }}
        ></i>
      ) : (
        <i className="fas fa-sort text-muted ms-1" style={{ fontSize: '0.7rem', opacity: 0.4 }}></i>
      )}
    </th>
  )

  useEffect(() => {
    if (userClaims?.customer) {
      setCustomerName(userClaims.customer)
      if (reportType === 'upi') {
        fetchTransactionData(userClaims.customer)
      } else {
        fetchSmartCardData(userClaims.customer)
      }
    }
  }, [userClaims, filters.dateRange, filters.customStartDate, filters.customEndDate, reportType])

  // Filter transactions when filters change
  useEffect(() => {
    if (reportType === 'upi') {
      applyFilters()
    } else {
      applySmartCardFilters()
    }
  }, [transactions, smartCardTransactions, filters.selectedDevices, filters.transactionType, filters.paymentMethod, filters.searchTerm, currentPage, smartCardCurrentPage, deviceObjects, reportType, upiSort, scSort])

  // Fetch device objects with location data
  useEffect(() => {
    const fetchDeviceObjects = async () => {
      if (!userClaims?.customer) return
      
      try {
        const deviceService = createDeviceService(userClaims)
        const deviceList = await deviceService.getAllDevices()
        
        // Apply device_restrictions for non-super-admin users
        const restrictions = userPermissions?.device_restrictions
        const restricted = !isSuperAdmin && Array.isArray(restrictions) && restrictions.length > 0
        const visibleDevices = restricted
          ? deviceList.filter(d => restrictions.includes(d.id))
          : deviceList

        console.log(`[Reports] Loaded ${visibleDevices.length} devices with location data`)
        setDeviceObjects(visibleDevices)
      } catch (err) {
        console.error('[Reports] Error fetching device objects:', err)
      }
    }
    
    fetchDeviceObjects()
  }, [userClaims])

  const getDateRange = () => {
    const formatDate = (date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return parseInt(`${year}${month}${day}`)
    }
    
    if (filters.dateRange === 'custom') {
      if (!filters.customStartDate || !filters.customEndDate) {
        return null // Invalid custom range
      }
      
      const startDate = new Date(filters.customStartDate)
      const endDate = new Date(filters.customEndDate)
      
      // Validate date range (min 1 day, max 90 days)
      const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))
      if (daysDiff < 0 || daysDiff > 90) {
        return null // Invalid range
      }
      
      return {
        start: formatDate(startDate),
        end: formatDate(endDate)
      }
    } else {
      // Handle preset periods
      if (filters.dateRange === 'today' || filters.dateRange === '1') {
        // Special case for "Today" - include only current date
        const today = new Date()
        return {
          start: formatDate(today),
          end: formatDate(today)
        }
      } else if (filters.dateRange === 'yesterday') {
        // Special case for "Yesterday" - include only yesterday's date
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        return {
          start: formatDate(yesterday),
          end: formatDate(yesterday)
        }
      } else {
        // Other preset periods - exclude today (historical data only)
        const endDate = new Date()
        endDate.setDate(endDate.getDate() - 1) // Exclude today
        const startDate = new Date()
        startDate.setDate(startDate.getDate() - parseInt(filters.dateRange))
        
        return {
          start: formatDate(startDate),
          end: formatDate(endDate)
        }
      }
    }
  }

  // Enhanced cache management with day-based caching
  const getDayCacheKey = (customer, date) => `reports_day_${customer}_${date}`
  
  const getRangeCacheKey = (customer, startDate, endDate) => 
    `reports_range_${customer}_${startDate}_${endDate}`

  const getCachedDayData = (customer, date) => {
    try {
      const cacheKey = getDayCacheKey(customer, date)
      const cached = localStorage.getItem(cacheKey)
      if (!cached) return null

      const data = JSON.parse(cached)
      const now = Date.now()
      
      // Today's data: cache for 5 minutes, historical data: cache for 24 hours
      const today = parseInt(new Date().toISOString().slice(0, 10).replace(/-/g, ''))
      const isToday = date === today
      const cacheValidityMs = isToday ? (5 * 60 * 1000) : (24 * 60 * 60 * 1000)
      const isValid = (now - data.timestamp) < cacheValidityMs
      
      if (isValid && data.transactions) {
        // Reconstruct Date objects from timestamps
        data.transactions.forEach(txn => {
          txn.datetime = new Date(txn.timestamp * 1000)
          txn.date = parseDate(date.toString())
        })
        return data
      }
      
      // Remove invalid cache
      localStorage.removeItem(cacheKey)
      return null
    } catch (error) {
      console.error('Cache read error:', error)
      return null
    }
  }

  const setCachedDayData = (customer, date, transactions, devices, paymentMethods) => {
    try {
      const cacheKey = getDayCacheKey(customer, date)
      console.log(`DEBUG: Attempting to cache data for key: ${cacheKey}`)
      
      // Skip caching for datasets that are too large (>10K transactions per day)
      if (transactions.length > 10000) {
        console.log(`DEBUG: Skipping cache for date ${date} - too many transactions (${transactions.length})`)
        return // Exit without caching
      }
      
      // Prepare compressed data for smaller datasets
      const compressedTransactions = transactions.map(txn => ({
        id: txn.id,
        timestamp: txn.timestamp,
        deviceId: txn.deviceId,
        type: txn.type,
        amount: txn.amount,
        selection: txn.selection,
        selectionHex: txn.selectionHex,
        paymentMethod: txn.paymentMethod,
        transactionId: txn.transactionId,
        productName: txn.productName,
        status: txn.status,
        refundReason: txn.refundReason
      }))
      
      const data = {
        timestamp: Date.now(),
        transactions: compressedTransactions,
        devices,
        paymentMethods
      }
      
      const serializedData = JSON.stringify(data)
      console.log(`DEBUG: Serialized data size: ${serializedData.length} characters`)
      
      localStorage.setItem(cacheKey, serializedData)
      console.log(`DEBUG: Successfully cached ${transactions.length} transactions for ${cacheKey}`)
      
      // Clean up old cache entries to prevent storage bloat
      const keys = Object.keys(localStorage).filter(key => key.startsWith('reports_'))
      if (keys.length > 50) {
        // Remove oldest cache entries
        keys.sort().slice(0, 20).forEach(key => localStorage.removeItem(key))
      }
    } catch (error) {
      console.error('Cache write error:', error.message, error)
      // If storage is full, clear cache aggressively
      if (error.name === 'QuotaExceededError') {
        console.log('DEBUG: Storage quota exceeded, clearing cache aggressively')
        
        // Clear ALL reports cache to make room
        const allKeys = Object.keys(localStorage)
        const reportsKeys = allKeys.filter(key => key.startsWith('reports_'))
        console.log(`DEBUG: Clearing ${reportsKeys.length} cache entries`)
        reportsKeys.forEach(key => localStorage.removeItem(key))
        
        // Also clear any other large cache entries to make room
        const analyticsKeys = allKeys.filter(key => key.startsWith('analytics_'))
        analyticsKeys.slice(0, 5).forEach(key => localStorage.removeItem(key))
        
        // Try to cache again after aggressive clearing
        try {
          const smallerData = JSON.stringify(data)
          localStorage.setItem(cacheKey, smallerData)
          console.log(`DEBUG: Successfully cached after clearing storage (${smallerData.length} chars)`)
        } catch (retryError) {
          console.warn('Cache write still failed after clearing - data too large for localStorage:', retryError.message)
          // Don't throw error - continue without caching this day
        }
      }
    }
  }

  const generateDateList = (startDate, endDate) => {
    const dates = []
    const start = new Date()
    const end = new Date()
    
    // Parse dates from integer format (YYYYMMDD)
    const startStr = startDate.toString()
    const endStr = endDate.toString()
    
    start.setFullYear(parseInt(startStr.substring(0, 4)))
    start.setMonth(parseInt(startStr.substring(4, 6)) - 1)
    start.setDate(parseInt(startStr.substring(6, 8)))
    
    end.setFullYear(parseInt(endStr.substring(0, 4)))
    end.setMonth(parseInt(endStr.substring(4, 6)) - 1)
    end.setDate(parseInt(endStr.substring(6, 8)))
    
    const current = new Date(start)
    while (current <= end) {
      const year = current.getFullYear()
      const month = String(current.getMonth() + 1).padStart(2, '0')
      const day = String(current.getDate()).padStart(2, '0')
      dates.push(parseInt(`${year}${month}${day}`))
      current.setDate(current.getDate() + 1)
    }
    
    return dates
  }

  const fetchTransactionData = async (customer) => {
    if (!customer) return

    setLoading(true)
    setError(null)
    
    try {
      const range = getDateRange()
      if (!range) {
        setError('Invalid date range. Please select a valid date range (1-90 days).')
        setLoading(false)
        return
      }
      
      console.log(`DEBUG: Fetching transactions for ${customer} from ${range.start} to ${range.end}`)
      console.log(`DEBUG: Current localStorage keys:`, Object.keys(localStorage).filter(k => k.startsWith('reports_')))
      
      // Generate list of dates in the range
      const dateList = generateDateList(range.start, range.end)
      console.log(`DEBUG: Generated date list:`, dateList)
      
      // Check which days are already cached
      const cachedDays = new Map()
      const uncachedDates = []
      
      for (const date of dateList) {
        const dayData = getCachedDayData(customer, date)
        if (dayData) {
          cachedDays.set(date, dayData)
        } else {
          uncachedDates.push(date)
        }
      }
      
      console.log(`Found ${cachedDays.size} cached days, need to fetch ${uncachedDates.length} days`)
      
      // Fetch uncached dates
      const uncachedData = new Map()
      for (const date of uncachedDates) {
        try {
          console.log(`Fetching data for date ${date}`)
          const dayResult = await fetchDateRange(customer, date, date)
          
          // Cache this day's data
          setCachedDayData(customer, date, dayResult.transactions, 
                           Array.from(dayResult.devices), Array.from(dayResult.paymentMethods))
          
          uncachedData.set(date, dayResult)
        } catch (err) {
          console.error(`Error fetching data for date ${date}:`, err)
          // Continue with other dates even if one fails
        }
      }
      
      // Combine all data (cached + newly fetched)
      let allTransactions = []
      const allDevices = new Set()
      const allPaymentMethods = new Set()
      
      // Add cached data
      for (const [date, dayData] of cachedDays) {
        allTransactions.push(...dayData.transactions)
        dayData.devices.forEach(d => allDevices.add(d))
        dayData.paymentMethods.forEach(p => allPaymentMethods.add(p))
      }
      
      // Add newly fetched data
      for (const [date, dayData] of uncachedData) {
        allTransactions.push(...dayData.transactions)
        dayData.devices.forEach(d => allDevices.add(d))
        dayData.paymentMethods.forEach(p => allPaymentMethods.add(p))
      }
      
      // Sort all transactions by datetime descending
      allTransactions.sort((a, b) => b.datetime - a.datetime)

      // Apply device_restrictions for non-super-admin users
      const restrictions = userPermissions?.device_restrictions
      const restricted = !isSuperAdmin && Array.isArray(restrictions) && restrictions.length > 0
      const visibleTransactions = restricted
        ? allTransactions.filter(txn => restrictions.includes(txn.deviceId))
        : allTransactions
      const visibleDevices = restricted
        ? Array.from(allDevices).filter(id => restrictions.includes(id)).sort()
        : Array.from(allDevices).sort()

      const paymentMethodArray = Array.from(allPaymentMethods).sort()
      
      setTransactions(visibleTransactions)
      setDevices(visibleDevices)
      setAvailablePaymentMethods(paymentMethodArray)
      
      console.log(`Loaded ${allTransactions.length} transactions (${cachedDays.size} cached days, ${uncachedDates.length} fetched days)`)
      console.log(`DEBUG: Final localStorage keys:`, Object.keys(localStorage).filter(k => k.startsWith('reports_')))
      
    } catch (err) {
      console.error('Error fetching transaction data:', err)
      setError(`Failed to fetch transaction data: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  // Helper function to fetch data for a specific date range
  const fetchDateRange = async (customer, startDate, endDate) => {
    const cashTxnRef = collection(db, 'cash_txn')
    const q = query(
      cashTxnRef,
      where('customer', '==', customer),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'desc')
    )
    
    const snapshot = await getDocs(q)
    const transactions = []
    const devices = new Set()
    const paymentMethods = new Set()
      
    snapshot.forEach((doc) => {
      const data = doc.data()
      const deviceId = data.devname
      const date = data.date
      const dateObj = parseDate(date.toString())
      
      devices.add(deviceId)
      
      // Process cash transactions
      if (data.cash_txn) {
        Object.entries(data.cash_txn).forEach(([timestamp, transactionData]) => {
          if (timestamp !== 'Total' && Array.isArray(transactionData)) {
            const [price, selection, selectionHex] = transactionData
            transactions.push({
              id: `cash_${deviceId}_${timestamp}`,
              timestamp: parseInt(timestamp),
              datetime: new Date(parseInt(timestamp) * 1000),
              deviceId,
              date: dateObj,
              type: 'cash',
              amount: price,
              selection,
              selectionHex,
              paymentMethod: 'cash',
              transactionId: null,
              productName: '',
              status: 'success',
              refundReason: null
            })
            paymentMethods.add('cash')
          }
        })
      }
      
      // Process PG transactions
      if (data.pg_txn) {
        Object.entries(data.pg_txn).forEach(([timestamp, transactionData]) => {
          if (timestamp !== 'Total' && Array.isArray(transactionData)) {
            const [price, selection, paymentGateway, selectionHex, transactionId, productName] = transactionData
            transactions.push({
              id: `pg_${deviceId}_${timestamp}`,
              timestamp: parseInt(timestamp),
              datetime: new Date(parseInt(timestamp) * 1000),
              deviceId,
              date: dateObj,
              type: 'pg',
              amount: price,
              selection,
              selectionHex,
              paymentMethod: paymentGateway,
              transactionId,
              productName: productName || '',
              status: 'success',
              refundReason: null
            })
            paymentMethods.add(paymentGateway)
          }
        })
      }
      
      // Process refund transactions
      if (data.refund_txn) {
        Object.entries(data.refund_txn).forEach(([timestamp, transactionData]) => {
          if (timestamp !== 'Total' && Array.isArray(transactionData)) {
            const [price, paymentGateway, originalTransactionId, refundReason] = transactionData
            transactions.push({
              id: `refund_${deviceId}_${timestamp}`,
              timestamp: parseInt(timestamp),
              datetime: new Date(parseInt(timestamp) * 1000),
              deviceId,
              date: dateObj,
              type: 'refund',
              amount: price,
              selection: null,
              selectionHex: null,
              paymentMethod: paymentGateway,
              transactionId: originalTransactionId,
              productName: '',
              status: 'refunded',
              refundReason
            })
            paymentMethods.add(paymentGateway)
          }
        })
      }
    })
    
    return {
      transactions,
      devices,
      paymentMethods
    }
  }

  const parseDate = (dateStr) => {
    const year = dateStr.substring(0, 4)
    const month = dateStr.substring(4, 6)
    const day = dateStr.substring(6, 8)
    return new Date(`${year}-${month}-${day}`)
  }

  const applyFilters = () => {
    let filtered = [...transactions]
    
    // Device filter
    if (filters.selectedDevices.length > 0) {
      filtered = filtered.filter(txn => filters.selectedDevices.includes(txn.deviceId))
    }
    
    // Transaction type filter
    if (filters.transactionType !== 'all') {
      filtered = filtered.filter(txn => txn.type === filters.transactionType)
    }
    
    // Payment method filter
    if (filters.paymentMethod !== 'all') {
      filtered = filtered.filter(txn => txn.paymentMethod === filters.paymentMethod)
    }
    
    // Search term filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase()
      filtered = filtered.filter(txn => {
        const deviceLocation = getDeviceLocation(txn.deviceId)
        return (
          txn.deviceId?.toLowerCase?.().includes(searchLower) ||
          txn.transactionId?.toLowerCase?.().includes(searchLower) ||
          txn.productName?.toLowerCase?.().includes(searchLower) ||
          String(txn.selectionHex || '').toLowerCase().includes(searchLower) ||
          txn.refundReason?.toLowerCase?.().includes(searchLower) ||
          deviceLocation?.toLowerCase?.().includes(searchLower)
        )
      })
    }
    
    // Set complete filtered data (for summary cards and summary view grouping)
    setFilteredTransactions(filtered)
    
    // Apply sort for the detailed table
    const upiValueGetter = (txn, col) => {
      if (col === 'datetime') return txn.datetime
      if (col === 'deviceId') return txn.deviceId
      if (col === 'location') return getDeviceLocation(txn.deviceId)
      if (col === 'type') return txn.type
      if (col === 'amount') return txn.amount
      if (col === 'selection') return txn.selection
      if (col === 'productName') return txn.productName
      if (col === 'paymentMethod') return txn.paymentMethod
      if (col === 'transactionId') return txn.transactionId
      return ''
    }
    const sorted = getSortedData(filtered, upiSort, upiValueGetter)

    // Calculate pagination
    const total = Math.ceil(sorted.length / itemsPerPage)
    setTotalPages(total)
    
    // Apply pagination (for table display)
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    setPaginatedTransactions(sorted.slice(startIndex, endIndex))
  }

  const fetchSmartCardData = async (customer) => {
    if (!customer) return

    setSmartCardLoading(true)
    setError(null)
    
    try {
      const range = getDateRange()
      if (!range) {
        setError('Invalid date range. Please select a valid date range (1-90 days).')
        setSmartCardLoading(false)
        return
      }
      
      console.log(`[SmartCard] Fetching data for ${customer} from ${range.start} to ${range.end}`)
      
      const smartCardService = createSmartCardService(userClaims)
      const result = await smartCardService.getTransactions(range.start, range.end)
      
      // Apply device_restrictions for non-super-admin users
      const restrictions = userPermissions?.device_restrictions
      const restricted = !isSuperAdmin && Array.isArray(restrictions) && restrictions.length > 0
      const visibleTransactions = restricted
        ? result.transactions.filter(txn => restrictions.includes(txn.deviceId))
        : result.transactions
      const visibleDevices = restricted
        ? result.devices.filter(id => restrictions.includes(id))
        : result.devices

      setSmartCardTransactions(visibleTransactions)
      setSmartCardDevices(visibleDevices)
      setFilteredSmartCardTransactions(visibleTransactions)
      setPaginatedSmartCardTransactions(visibleTransactions.slice(0, itemsPerPage))
      setSmartCardTotalPages(Math.ceil(visibleTransactions.length / itemsPerPage))
      
      console.log(`[SmartCard] Loaded ${visibleTransactions.length} transactions${restricted ? ` (restricted from ${result.transactions.length})` : ''}`)
      
    } catch (err) {
      console.error('[SmartCard] Error fetching data:', err)
      setError(`Failed to fetch smart card data: ${err.message}`)
    } finally {
      setSmartCardLoading(false)
    }
  }

  const applySmartCardFilters = () => {
    let filtered = [...smartCardTransactions]
    
    // Device filter
    if (filters.selectedDevices.length > 0) {
      filtered = filtered.filter(txn => filters.selectedDevices.includes(txn.deviceId))
    }
    
    // Search term filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase()
      filtered = filtered.filter(txn => {
        return (
          txn.deviceId?.toLowerCase?.().includes(searchLower) ||
          txn.location?.toLowerCase?.().includes(searchLower) ||
          txn.employee?.toLowerCase?.().includes(searchLower) ||
          txn.cardHolderName?.toLowerCase?.().includes(searchLower) ||
          String(txn.displayCardNumber || '').toLowerCase().includes(searchLower) ||
          txn.cardId?.toLowerCase?.().includes(searchLower) ||
          txn.productName?.toLowerCase?.().includes(searchLower) ||
          txn.selection?.toLowerCase?.().includes(searchLower)
        )
      })
    }
    
    setFilteredSmartCardTransactions(filtered)

    // Apply sort for smart card table
    const scValueGetter = (txn, col) => {
      if (col === 'datetime') return txn.datetime
      if (col === 'deviceId') return txn.deviceId
      if (col === 'location') return txn.location
      if (col === 'employee') return txn.employee
      if (col === 'cardHolderName') return txn.cardHolderName
      if (col === 'displayCardNumber') return txn.displayCardNumber
      if (col === 'cardId') return txn.cardId
      if (col === 'selection') return txn.selection
      if (col === 'productName') return txn.productName
      if (col === 'amount') return txn.amount
      return ''
    }
    const sortedSc = getSortedData(filtered, scSort, scValueGetter)
    
    // Calculate pagination
    const total = Math.ceil(sortedSc.length / itemsPerPage)
    setSmartCardTotalPages(total)
    
    // Apply pagination
    const startIndex = (smartCardCurrentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    setPaginatedSmartCardTransactions(sortedSc.slice(startIndex, endIndex))
  }

  const exportSmartCardToCSV = () => {
    const headers = [
      'Timestamp',
      'Device ID',
      'Location',
      'Employee',
      'Card Holder',
      'Display Card Number',
      'Card ID',
      'Selection',
      'Product Name',
      'Amount',
      'Credited Amount',
      'Max Daily',
      'Transaction ID'
    ]
    
    const csvData = filteredSmartCardTransactions.map(txn => [
      formatDateTime(txn.datetime),
      txn.deviceId,
      txn.location,
      txn.employee,
      txn.cardHolderName,
      txn.displayCardNumber,
      txn.cardId,
      txn.selection,
      txn.productName,
      txn.amount,
      txn.creditedAmount,
      txn.maxDaily,
      txn.txnId
    ])
    
    const csvContent = [headers, ...csvData]
      .map((row, rowIndex) => {
        return row.map((field, colIndex) => {
          // Format Transaction ID column (last column) as text
          if (rowIndex > 0 && colIndex === 12 && field) {
            return `"=""${field}"""`
          }
          return `"${field}"`
        }).join(',')
      })
      .join('\n')
    
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `smartcard_transactions_${customerName}_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }))
    setCurrentPage(1)
    setSmartCardCurrentPage(1)
  }

  const handleDeviceSelection = (deviceId, isChecked) => {
    setFilters(prev => {
      const newSelectedDevices = isChecked 
        ? [...prev.selectedDevices, deviceId]
        : prev.selectedDevices.filter(id => id !== deviceId)
      return { ...prev, selectedDevices: newSelectedDevices }
    })
    setCurrentPage(1)
    setSmartCardCurrentPage(1)
  }

  const handleSelectAllDevices = () => {
    setFilters(prev => ({ ...prev, selectedDevices: [...devices] }))
    setCurrentPage(1)
    setSmartCardCurrentPage(1)
  }

  const handleSelectNoDevices = () => {
    setFilters(prev => ({ ...prev, selectedDevices: [] }))
    setCurrentPage(1)
    setSmartCardCurrentPage(1)
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount)
  }

  const formatDateTime = (datetime) => {
    return datetime.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const formatDate = (date) => {
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      weekday: 'short'
    })
  }

  // Group transactions by date for summary view
  const groupTransactionsByDate = (transactions) => {
    const dateGroups = {}
    
    transactions.forEach(transaction => {
      const dateKey = transaction.date.toISOString().split('T')[0] // YYYY-MM-DD
      
      if (!dateGroups[dateKey]) {
        dateGroups[dateKey] = {
          date: dateKey,
          dateObj: transaction.date,
          totalRevenue: 0,
          transactionCount: 0,
          cashCount: 0,
          pgCount: 0,
          refundCount: 0,
          refundAmount: 0,
          devices: {},
          paymentMethods: new Set()
        }
      }
      
      const group = dateGroups[dateKey]
      group.transactionCount += 1
      group.paymentMethods.add(transaction.paymentMethod)
      
      // Track per-device breakdown
      if (!group.devices[transaction.deviceId]) {
        group.devices[transaction.deviceId] = {
          revenue: 0,
          count: 0,
          cashCount: 0,
          pgCount: 0,
          refundCount: 0
        }
      }
      
      const deviceGroup = group.devices[transaction.deviceId]
      deviceGroup.count += 1
      
      // Handle different transaction types
      if (transaction.type === 'refund') {
        group.refundCount += 1
        group.refundAmount += transaction.amount
        deviceGroup.refundCount += 1
      } else {
        group.totalRevenue += transaction.amount
        deviceGroup.revenue += transaction.amount
        
        if (transaction.type === 'cash') {
          group.cashCount += 1
          deviceGroup.cashCount += 1
        } else if (transaction.type === 'pg') {
          group.pgCount += 1
          deviceGroup.pgCount += 1
        }
      }
    })
    
    // Convert to array and sort by date (newest first)
    return Object.values(dateGroups).sort((a, b) => new Date(b.date) - new Date(a.date))
  }

  const toggleDateExpansion = (dateKey) => {
    const newExpanded = new Set(expandedDates)
    if (newExpanded.has(dateKey)) {
      newExpanded.delete(dateKey)
    } else {
      newExpanded.add(dateKey)
    }
    setExpandedDates(newExpanded)
  }

  const getTransactionTypeIcon = (type) => {
    switch (type) {
      case 'cash': return 'fas fa-money-bill text-success'
      case 'pg': return 'fas fa-credit-card text-primary'
      case 'refund': return 'fas fa-undo text-danger'
      default: return 'fas fa-exchange-alt text-muted'
    }
  }

  // Helper function to get device location by ID
  const getDeviceLocation = (deviceId) => {
    const device = deviceObjects.find(d => d.id === deviceId)
    return device?.location || ''
  }

  const exportSummaryToCSV = () => {
    const headers = [
      'Date',
      'Day of Week',
      'Device ID',
      'Location',
      'Revenue',
      'Transactions',
      'Cash Transactions',
      'PG Transactions',
      'Refund Count'
    ]
    
    const groupedData = groupTransactionsByDate(filteredTransactions)
    
    // Flatten the data: create a row for each device-date combination
    const csvData = []
    groupedData.forEach(dateGroup => {
      const dayOfWeek = formatDate(dateGroup.dateObj).split(',')[0]
      
      // Create a row for each device on this date
      Object.entries(dateGroup.devices).forEach(([deviceId, deviceData]) => {
        csvData.push([
          dateGroup.date,
          dayOfWeek,
          deviceId,
          getDeviceLocation(deviceId),
          deviceData.revenue,
          deviceData.count,
          deviceData.cashCount,
          deviceData.pgCount,
          deviceData.refundCount
        ])
      })
    })
    
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n')
    
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `summary_by_device_date_${customerName}_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const exportToCSV = () => {
    // If in summary view, export summary data instead of detailed transactions
    if (viewMode === 'summary') {
      exportSummaryToCSV()
      return
    }
    
    const headers = [
      'Timestamp',
      'Device ID',
      'Location',
      'Transaction Type',
      'Amount',
      'Selection',
      'Payment Method',
      'Transaction ID',
      'Product Name',
      'Status',
      'Refund Reason'
    ]
    
    const csvData = filteredTransactions.map(txn => [
      formatDateTime(txn.datetime),
      txn.deviceId,
      getDeviceLocation(txn.deviceId),
      txn.type.toUpperCase(),
      txn.amount,
      txn.selection || '',
      txn.paymentMethod,
      txn.transactionId || '',
      txn.productName,
      txn.status,
      txn.refundReason || ''
    ])
    
    const csvContent = [headers, ...csvData]
      .map((row, rowIndex) => {
        return row.map((field, colIndex) => {
          // Format Transaction ID column (index 7) as text to prevent scientific notation in Excel
          if (rowIndex > 0 && colIndex === 7 && field) {
            // Use ="value" format to force Excel to treat it as text
            return `"=""${field}"""`
          }
          return `"${field}"`
        }).join(',')
      })
      .join('\n')
    
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `transactions_${customerName}_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  if (!userClaims) {
    return (
      <div className="alert alert-info">
        <i className="fas fa-info-circle me-2"></i>
        Loading user claims...
      </div>
    )
  }

  if (!customerName) {
    return (
      <div className="alert alert-warning">
        <i className="fas fa-exclamation-triangle me-2"></i>
        No customer name found in Firebase claims. Expected claim: "customer"
      </div>
    )
  }

  return (
    <div className="container-fluid px-4 py-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 mb-0">
            Transaction Reports
            <AlertBadge category="system" className="ms-2" />
          </h1>
          <p className="text-muted mb-0">Detailed transaction history and analytics</p>
        </div>
        <div className="d-flex gap-2">
          <button 
            className="btn btn-outline-success btn-sm"
            onClick={reportType === 'smartcard' ? exportSmartCardToCSV : exportToCSV}
            disabled={reportType === 'smartcard' ? filteredSmartCardTransactions.length === 0 : filteredTransactions.length === 0}
          >
            <i className="fas fa-download me-1"></i>
            Export CSV
          </button>
        </div>
      </div>

      {/* Device restriction info banner */}
      {!isSuperAdmin && Array.isArray(userPermissions?.device_restrictions) && userPermissions.device_restrictions.length > 0 && (
        <div className="alert alert-info py-2 mb-3">
          <i className="fas fa-filter me-2"></i>
          Results are limited to your {userPermissions.device_restrictions.length} assigned device{userPermissions.device_restrictions.length !== 1 ? 's' : ''}.
        </div>
      )}

      {/* Report Type Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button 
            className={`nav-link ${reportType === 'upi' ? 'active' : ''}`}
            onClick={() => { setReportType('upi'); setCurrentPage(1); }}
          >
            <i className="fas fa-mobile-alt me-2"></i>
            UPI / Cash
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${reportType === 'smartcard' ? 'active' : ''}`}
            onClick={() => { setReportType('smartcard'); setSmartCardCurrentPage(1); }}
          >
            <i className="fas fa-id-card me-2"></i>
            Smart Card
          </button>
        </li>
      </ul>

      {/* UPI/Cash Filters - only show when UPI tab is active */}
      {reportType === 'upi' && (
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-2">
              <label className="form-label">Period</label>
              <div className="d-flex gap-2">
                <select 
                  className="form-select form-select-sm"
                  value={filters.dateRange}
                  onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                >
                  <option value="1">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                  <option value="custom">Custom Range</option>
                </select>
                {(filters.dateRange === '1' || filters.dateRange === 'today') && (
                  <button 
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => fetchTransactionData(customerName)}
                    disabled={loading}
                    title="Refresh today's data"
                  >
                    {loading ? (
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : (
                      <i className="fas fa-sync-alt"></i>
                    )}
                  </button>
                )}
              </div>
            </div>
            
            {filters.dateRange === 'custom' && (
              <>
                <div className="col-md-2">
                  <label className="form-label">Start Date</label>
                  <input 
                    type="date"
                    className="form-control form-control-sm"
                    value={filters.customStartDate}
                    onChange={(e) => handleFilterChange('customStartDate', e.target.value)}
                    max={new Date().toISOString().split('T')[0]} // Can't select future dates
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">End Date</label>
                  <input 
                    type="date"
                    className="form-control form-control-sm"
                    value={filters.customEndDate}
                    onChange={(e) => handleFilterChange('customEndDate', e.target.value)}
                    min={filters.customStartDate} // End date must be after start date
                    max={new Date().toISOString().split('T')[0]} // Can't select future dates
                  />
                </div>
              </>
            )}
            
            <div className="col-md-3">
              <label className="form-label">
                Devices 
                <small className="text-muted">({filters.selectedDevices.length} selected)</small>
              </label>
              <div className="border rounded p-2" style={{maxHeight: '120px', overflowY: 'auto'}}>
                <div className="d-flex gap-2 mb-2">
                  <button 
                    type="button" 
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleSelectAllDevices}
                  >
                    All
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleSelectNoDevices}
                  >
                    None
                  </button>
                </div>
                {devices.map(device => (
                  <div key={device} className="form-check form-check-sm">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id={`device-${device}`}
                      checked={filters.selectedDevices.includes(device)}
                      onChange={(e) => handleDeviceSelection(device, e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor={`device-${device}`}>
                      {device}
                    </label>
                  </div>
                ))}
                {devices.length === 0 && (
                  <small className="text-muted">No devices available</small>
                )}
              </div>
            </div>
            
            <div className="col-md-2">
              <label className="form-label">Type</label>
              <select 
                className="form-select form-select-sm"
                value={filters.transactionType}
                onChange={(e) => handleFilterChange('transactionType', e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="cash">Cash</option>
                <option value="pg">Payment Gateway</option>
                <option value="refund">Refunds</option>
              </select>
            </div>
            
            <div className="col-md-2">
              <label className="form-label">Payment</label>
              <select 
                className="form-select form-select-sm"
                value={filters.paymentMethod}
                onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
              >
                <option value="all">All Methods</option>
                {availablePaymentMethods.map(method => (
                  <option key={method} value={method}>
                    {method.charAt(0).toUpperCase() + method.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="col-md-4">
              <label className="form-label">Search</label>
              <input 
                type="text" 
                className="form-control form-control-sm"
                placeholder="Device, Location, Transaction ID, Product..."
                value={filters.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Summary Stats */}
      {!loading && transactions.length > 0 && reportType === 'upi' && (
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center">
                <h4 className="text-primary">{filteredTransactions.length}</h4>
                <small className="text-muted">Total Transactions</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center">
                <h4 className="text-success">
                  {formatCurrency(filteredTransactions.reduce((sum, txn) => 
                    txn.type !== 'refund' ? sum + txn.amount : sum, 0))}
                </h4>
                <small className="text-muted">Total Revenue</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center">
                <h4 className="text-info">{new Set(filteredTransactions.map(txn => txn.deviceId)).size}</h4>
                <small className="text-muted">Active Devices</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center">
                <h4 className="text-warning">
                  {filteredTransactions.filter(txn => txn.type === 'refund').length}
                </h4>
                <small className="text-muted">Refunds</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Toggle Buttons */}
      {!loading && transactions.length > 0 && reportType === 'upi' && (
        <div className="d-flex justify-content-center mb-4">
          <div className="btn-group" role="group">
            <button 
              type="button" 
              className={`btn ${
                viewMode === 'detailed' ? 'btn-primary' : 'btn-outline-primary'
              }`}
              onClick={() => setViewMode('detailed')}
            >
              <i className="fas fa-list me-1"></i>
              Detailed View
            </button>
            <button 
              type="button" 
              className={`btn ${
                viewMode === 'summary' ? 'btn-primary' : 'btn-outline-primary'
              }`}
              onClick={() => setViewMode('summary')}
            >
              <i className="fas fa-calendar-alt me-1"></i>
              Summary by Date
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <strong>Error:</strong> {error}
        </div>
      )}

      {reportType === 'upi' && (loading ? (
        <div className="text-center py-5">
          <div className="spinner-border mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div>Loading transaction data...</div>
        </div>
      ) : transactions.length === 0 ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-5">
            <i className="fas fa-receipt fa-3x mb-3 text-muted opacity-50"></i>
            <h5 className="text-muted">No Transaction Data Available</h5>
            <p className="text-muted mb-0">
              No transactions found for the selected period.
            </p>
          </div>
        </div>
      ) : (
        viewMode === 'detailed' ? (
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <i className="fas fa-list me-2"></i>
                Transaction Details ({filteredTransactions.length} of {transactions.length})
              </h6>
              {totalPages > 1 && (
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button 
                        className="page-link"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      >
                        Previous
                      </button>
                    </li>
                    <li className="page-item active">
                      <span className="page-link">
                        {currentPage} of {totalPages}
                      </span>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button 
                        className="page-link"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <SortableHeader label="Timestamp" column="datetime" sortState={upiSort} onSort={(col) => handleSortToggle(setUpiSort, col, () => setCurrentPage(1))} />
                    <SortableHeader label="Device" column="deviceId" sortState={upiSort} onSort={(col) => handleSortToggle(setUpiSort, col, () => setCurrentPage(1))} />
                    <SortableHeader label="Location" column="location" sortState={upiSort} onSort={(col) => handleSortToggle(setUpiSort, col, () => setCurrentPage(1))} />
                    <SortableHeader label="Type" column="type" sortState={upiSort} onSort={(col) => handleSortToggle(setUpiSort, col, () => setCurrentPage(1))} />
                    <SortableHeader label="Amount" column="amount" sortState={upiSort} onSort={(col) => handleSortToggle(setUpiSort, col, () => setCurrentPage(1))} />
                    <SortableHeader label="Selection" column="selection" sortState={upiSort} onSort={(col) => handleSortToggle(setUpiSort, col, () => setCurrentPage(1))} />
                    <SortableHeader label="Product Name" column="productName" sortState={upiSort} onSort={(col) => handleSortToggle(setUpiSort, col, () => setCurrentPage(1))} />
                    <SortableHeader label="Payment" column="paymentMethod" sortState={upiSort} onSort={(col) => handleSortToggle(setUpiSort, col, () => setCurrentPage(1))} />
                    <SortableHeader label="Transaction ID" column="transactionId" sortState={upiSort} onSort={(col) => handleSortToggle(setUpiSort, col, () => setCurrentPage(1))} />
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTransactions.map((txn) => (
                    <tr key={txn.id}>
                      <td>
                        <small>{formatDateTime(txn.datetime)}</small>
                      </td>
                      <td>
                        <code className="text-primary">{txn.deviceId}</code>
                      </td>
                      <td>
                        {getDeviceLocation(txn.deviceId) ? (
                          <small className="text-muted">
                            <i className="fas fa-map-marker-alt me-1"></i>
                            {getDeviceLocation(txn.deviceId)}
                          </small>
                        ) : (
                          <small className="text-muted">—</small>
                        )}
                      </td>
                      <td>
                        <i className={getTransactionTypeIcon(txn.type)}></i>
                        <span className="ms-1">{txn.type.toUpperCase()}</span>
                      </td>
                      <td>
                        <strong className={txn.type === 'refund' ? 'text-danger' : 'text-success'}>
                          {txn.type === 'refund' ? '-' : ''}{formatCurrency(txn.amount)}
                        </strong>
                      </td>
                      <td>
                        {txn.selection && (
                          <div>
                            <span className="badge bg-light text-dark">{txn.selection}</span>
                            {txn.selectionHex && (
                              <small className="text-muted ms-1">({txn.selectionHex})</small>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {txn.productName ? (
                          <small className="text-dark">{txn.productName}</small>
                        ) : (
                          <small className="text-muted">—</small>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${
                          txn.paymentMethod === 'cash' ? 'bg-success' : 
                          txn.paymentMethod === 'paytm' ? 'bg-primary' : 
                          txn.paymentMethod === 'phonepe' ? 'text-white' : 'bg-info'
                        }`}
                        style={{
                          backgroundColor: txn.paymentMethod === 'phonepe' ? '#5f259f' : undefined
                        }}>
                          {txn.paymentMethod}
                        </span>
                      </td>
                      <td>
                        {txn.transactionId ? (
                          <small className="text-muted font-monospace" 
                                 style={{wordBreak: 'break-all', fontSize: '0.75rem'}}>
                            {txn.transactionId}
                          </small>
                        ) : (
                          <small className="text-muted">—</small>
                        )}
                      </td>
                      <td>
                        {txn.type === 'refund' ? (
                          <div>
                            <span className="badge bg-danger">Refunded</span>
                            {txn.refundReason && (
                              <small className="d-block text-muted">{txn.refundReason}</small>
                            )}
                          </div>
                        ) : (
                          <span className="badge bg-success">Success</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Summary View by Date */}
          <div className="card border-0 shadow-sm">
          <div className="card-header bg-white">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <i className="fas fa-calendar-alt me-2"></i>
                Daily Summary ({groupTransactionsByDate(filteredTransactions).length} days)
              </h6>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <SortableHeader label="Date" column="date" sortState={summarySort} onSort={(col) => handleSortToggle(setSummarySort, col, () => {})} />
                    <SortableHeader label="Revenue" column="totalRevenue" sortState={summarySort} onSort={(col) => handleSortToggle(setSummarySort, col, () => {})} />
                    <SortableHeader label="Transactions" column="transactionCount" sortState={summarySort} onSort={(col) => handleSortToggle(setSummarySort, col, () => {})} />
                    <SortableHeader label="Cash" column="cashCount" sortState={summarySort} onSort={(col) => handleSortToggle(setSummarySort, col, () => {})} />
                    <SortableHeader label="PG" column="pgCount" sortState={summarySort} onSort={(col) => handleSortToggle(setSummarySort, col, () => {})} />
                    <SortableHeader label="Refunds" column="refundCount" sortState={summarySort} onSort={(col) => handleSortToggle(setSummarySort, col, () => {})} />
                    <SortableHeader label="Devices" column="deviceCount" sortState={summarySort} onSort={(col) => handleSortToggle(setSummarySort, col, () => {})} />
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedData(
                    groupTransactionsByDate(filteredTransactions),
                    summarySort,
                    (g, col) => {
                      if (col === 'date') return g.date
                      if (col === 'totalRevenue') return g.totalRevenue
                      if (col === 'transactionCount') return g.transactionCount
                      if (col === 'cashCount') return g.cashCount
                      if (col === 'pgCount') return g.pgCount
                      if (col === 'refundCount') return g.refundCount
                      if (col === 'deviceCount') return Object.keys(g.devices).length
                      return ''
                    }
                  ).map((dateGroup) => (
                    <React.Fragment key={dateGroup.date}>
                      <tr>
                        <td>
                          <div>
                            <strong>{formatDate(dateGroup.dateObj)}</strong>
                            <br/>
                            <small className="text-muted">{dateGroup.date}</small>
                          </div>
                        </td>
                        <td>
                          <strong className="text-success">
                            {formatCurrency(dateGroup.totalRevenue)}
                          </strong>
                          {dateGroup.refundAmount > 0 && (
                            <div>
                              <small className="text-danger">
                                -{formatCurrency(dateGroup.refundAmount)} refunded
                              </small>
                            </div>
                          )}
                        </td>
                        <td>
                          <span className="badge bg-primary">{dateGroup.transactionCount}</span>
                        </td>
                        <td>
                          <span className="badge bg-success">{dateGroup.cashCount}</span>
                        </td>
                        <td>
                          <span className="badge bg-info">{dateGroup.pgCount}</span>
                        </td>
                        <td>
                          <span className="badge bg-warning">{dateGroup.refundCount}</span>
                        </td>
                        <td>
                          <span className="badge bg-secondary">
                            {Object.keys(dateGroup.devices).length}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => toggleDateExpansion(dateGroup.date)}
                            title={expandedDates.has(dateGroup.date) ? 'Hide devices' : 'Show devices'}
                          >
                            <i className={`fas ${
                              expandedDates.has(dateGroup.date) ? 'fa-chevron-up' : 'fa-chevron-down'
                            }`}></i>
                          </button>
                        </td>
                      </tr>
                      {expandedDates.has(dateGroup.date) && (
                        <tr>
                          <td colSpan="8" className="bg-light">
                            <div className="p-3">
                              <h6 className="mb-2">Device Breakdown for {formatDate(dateGroup.dateObj)}</h6>
                              <div className="table-responsive">
                                <table className="table table-sm mb-0">
                                  <thead>
                                    <tr>
                                      <th>Device</th>
                                      <th>Location</th>
                                      <th>Revenue</th>
                                      <th>Transactions</th>
                                      <th>Cash</th>
                                      <th>PG</th>
                                      <th>Refunds</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {Object.entries(dateGroup.devices).map(([deviceId, deviceData]) => (
                                      <tr key={deviceId}>
                                        <td>
                                          <code className="text-primary">{deviceId}</code>
                                        </td>
                                        <td>
                                          {getDeviceLocation(deviceId) ? (
                                            <small className="text-muted">
                                              <i className="fas fa-map-marker-alt me-1"></i>
                                              {getDeviceLocation(deviceId)}
                                            </small>
                                          ) : (
                                            <small className="text-muted">—</small>
                                          )}
                                        </td>
                                        <td>
                                          <strong className="text-success">
                                            {formatCurrency(deviceData.revenue)}
                                          </strong>
                                        </td>
                                        <td>
                                          <span className="badge bg-primary">{deviceData.count}</span>
                                        </td>
                                        <td>
                                          <span className="badge bg-success">{deviceData.cashCount}</span>
                                        </td>
                                        <td>
                                          <span className="badge bg-info">{deviceData.pgCount}</span>
                                        </td>
                                        <td>
                                          <span className="badge bg-warning">{deviceData.refundCount}</span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        </>
      )
    ))}

      {/* Smart Card View */}
      {reportType === 'smartcard' && (
        <>
          {/* Smart Card Filters */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-2">
                  <label className="form-label">Period</label>
                  <select 
                    className="form-select form-select-sm"
                    value={filters.dateRange}
                    onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                  >
                    <option value="1">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="7">Last 7 days</option>
                    <option value="30">Last 30 days</option>
                    <option value="90">Last 90 days</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>
                
                {filters.dateRange === 'custom' && (
                  <>
                    <div className="col-md-2">
                      <label className="form-label">Start Date</label>
                      <input 
                        type="date"
                        className="form-control form-control-sm"
                        value={filters.customStartDate}
                        onChange={(e) => handleFilterChange('customStartDate', e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">End Date</label>
                      <input 
                        type="date"
                        className="form-control form-control-sm"
                        value={filters.customEndDate}
                        onChange={(e) => handleFilterChange('customEndDate', e.target.value)}
                        min={filters.customStartDate}
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </>
                )}
                
                <div className="col-md-3">
                  <label className="form-label">
                    Devices 
                    <small className="text-muted">({filters.selectedDevices.length} selected)</small>
                  </label>
                  <div className="border rounded p-2" style={{maxHeight: '120px', overflowY: 'auto'}}>
                    <div className="d-flex gap-2 mb-2">
                      <button 
                        type="button" 
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setFilters(prev => ({ ...prev, selectedDevices: [...smartCardDevices] }))}
                      >
                        All
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setFilters(prev => ({ ...prev, selectedDevices: [] }))}
                      >
                        None
                      </button>
                    </div>
                    {smartCardDevices.map(device => (
                      <div key={device} className="form-check form-check-sm">
                        <input 
                          className="form-check-input" 
                          type="checkbox" 
                          id={`sc-device-${device}`}
                          checked={filters.selectedDevices.includes(device)}
                          onChange={(e) => handleDeviceSelection(device, e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor={`sc-device-${device}`}>
                          {device}
                        </label>
                      </div>
                    ))}
                    {smartCardDevices.length === 0 && (
                      <small className="text-muted">No devices available</small>
                    )}
                  </div>
                </div>
                
                <div className="col-md-4">
                  <label className="form-label">Search</label>
                  <input 
                    type="text" 
                    className="form-control form-control-sm"
                    placeholder="Device, Location, Employee, Card Holder, Card Number..."
                    value={filters.searchTerm}
                    onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Smart Card Summary Stats */}
          {!smartCardLoading && smartCardTransactions.length > 0 && (
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h4 className="text-primary">{filteredSmartCardTransactions.length}</h4>
                    <small className="text-muted">Total Transactions</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h4 className="text-success">
                      {formatCurrency(filteredSmartCardTransactions.reduce((sum, txn) => sum + txn.amount, 0))}
                    </h4>
                    <small className="text-muted">Total Amount</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h4 className="text-info">{new Set(filteredSmartCardTransactions.map(txn => txn.deviceId)).size}</h4>
                    <small className="text-muted">Active Devices</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h4 className="text-warning">
                      {new Set(filteredSmartCardTransactions.map(txn => txn.cardId)).size}
                    </h4>
                    <small className="text-muted">Unique Cards</small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <strong>Error:</strong> {error}
            </div>
          )}

          {smartCardLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div>Loading smart card transactions...</div>
            </div>
          ) : smartCardTransactions.length === 0 ? (
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center py-5">
                <i className="fas fa-id-card fa-3x mb-3 text-muted opacity-50"></i>
                <h5 className="text-muted">No Smart Card Transactions</h5>
                <p className="text-muted mb-0">
                  No smart card transactions found for the selected period.
                </p>
              </div>
            </div>
          ) : (
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">
                    <i className="fas fa-id-card me-2"></i>
                    Smart Card Transactions ({filteredSmartCardTransactions.length} of {smartCardTransactions.length})
                  </h6>
                  {smartCardTotalPages > 1 && (
                    <nav>
                      <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${smartCardCurrentPage === 1 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link"
                            onClick={() => setSmartCardCurrentPage(prev => Math.max(1, prev - 1))}
                          >
                            Previous
                          </button>
                        </li>
                        <li className="page-item active">
                          <span className="page-link">
                            {smartCardCurrentPage} of {smartCardTotalPages}
                          </span>
                        </li>
                        <li className={`page-item ${smartCardCurrentPage === smartCardTotalPages ? 'disabled' : ''}`}>
                          <button 
                            className="page-link"
                            onClick={() => setSmartCardCurrentPage(prev => Math.min(smartCardTotalPages, prev + 1))}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}
                </div>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <SortableHeader label="Timestamp" column="datetime" sortState={scSort} onSort={(col) => handleSortToggle(setScSort, col, () => setSmartCardCurrentPage(1))} />
                        <SortableHeader label="Device" column="deviceId" sortState={scSort} onSort={(col) => handleSortToggle(setScSort, col, () => setSmartCardCurrentPage(1))} />
                        <SortableHeader label="Location" column="location" sortState={scSort} onSort={(col) => handleSortToggle(setScSort, col, () => setSmartCardCurrentPage(1))} />
                        <SortableHeader label="Employee" column="employee" sortState={scSort} onSort={(col) => handleSortToggle(setScSort, col, () => setSmartCardCurrentPage(1))} />
                        <SortableHeader label="Card Holder" column="cardHolderName" sortState={scSort} onSort={(col) => handleSortToggle(setScSort, col, () => setSmartCardCurrentPage(1))} />
                        <SortableHeader label="Display Card #" column="displayCardNumber" sortState={scSort} onSort={(col) => handleSortToggle(setScSort, col, () => setSmartCardCurrentPage(1))} />
                        <SortableHeader label="Card ID" column="cardId" sortState={scSort} onSort={(col) => handleSortToggle(setScSort, col, () => setSmartCardCurrentPage(1))} />
                        <SortableHeader label="Selection" column="selection" sortState={scSort} onSort={(col) => handleSortToggle(setScSort, col, () => setSmartCardCurrentPage(1))} />
                        <SortableHeader label="Product" column="productName" sortState={scSort} onSort={(col) => handleSortToggle(setScSort, col, () => setSmartCardCurrentPage(1))} />
                        <SortableHeader label="Amount" column="amount" sortState={scSort} onSort={(col) => handleSortToggle(setScSort, col, () => setSmartCardCurrentPage(1))} />
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedSmartCardTransactions.map((txn) => (
                        <tr key={txn.id}>
                          <td>
                            <small>{formatDateTime(txn.datetime)}</small>
                          </td>
                          <td>
                            <code className="text-primary">{txn.deviceId}</code>
                          </td>
                          <td>
                            {txn.location ? (
                              <small className="text-muted">
                                <i className="fas fa-map-marker-alt me-1"></i>
                                {txn.location}
                              </small>
                            ) : (
                              <small className="text-muted">—</small>
                            )}
                          </td>
                          <td>
                            {txn.employee ? (
                              <small className="text-dark">{txn.employee}</small>
                            ) : (
                              <small className="text-muted">—</small>
                            )}
                          </td>
                          <td>
                            {txn.cardHolderName ? (
                              <small className="text-dark">{txn.cardHolderName}</small>
                            ) : (
                              <small className="text-muted">—</small>
                            )}
                          </td>
                          <td>
                            {txn.displayCardNumber ? (
                              <code className="text-info">{txn.displayCardNumber}</code>
                            ) : (
                              <small className="text-muted">—</small>
                            )}
                          </td>
                          <td>
                            {txn.cardId ? (
                              <small className="font-monospace text-muted">{txn.cardId}</small>
                            ) : (
                              <small className="text-muted">—</small>
                            )}
                          </td>
                          <td>
                            {txn.selection && (
                              <span className="badge bg-light text-dark">{txn.selection}</span>
                            )}
                          </td>
                          <td>
                            {txn.productName ? (
                              <small className="text-dark">{txn.productName}</small>
                            ) : (
                              <small className="text-muted">—</small>
                            )}
                          </td>
                          <td>
                            <strong className="text-success">{formatCurrency(txn.amount)}</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}